import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { IconTrash } from "@tabler/icons-react";
import toast from "react-hot-toast";
import Select, { MultiValue } from "react-select";
import { VscFilter } from "react-icons/vsc";
import { TbFilterX } from "react-icons/tb";
import DeleteItem from "../DeleteItem";

import { deleteCourseRegistrationApi, getCourseRegistrationApi, updateCourseRegistrationStatusApi } from "../../../Api/Registration";
import { getCourseTypeApi } from "../../../Api/CourseType";
import { addFeeApi } from "../../../Api/Fee";
import { getCourseOfferingApi } from "../../../Api/CourseOffering";
import { formatDate } from "../../general/FormatDateTime";

interface Props {
  url: string;
}

export function Registration({ url }: Props) {

  interface User {
    id: string;
    name: string;
    mobile: string;
    email: string;
    address: string;
    roleType: number;
    gender: string | null;
    createdOn: string;
  }

  interface Course {
    id: string;
    name: string;
  }

  interface CourseType {
    id: string;
    name: string;
  }

  interface CourseRegistrations {
    id: string;
    courseOfferingId: string;
    userId: string;
    duration: number;
    timeZone: string;
    timeRange: string;
    status: string;
    fee: number;
    user: User;
    course: Course;
    courseType: CourseType;
    updatedBy: string;
    date: string;
    paidOn: string;
  }

  interface User {
    id: string;
    name: string;
    email: string;
    phone: string;
  }
  const [filterModal, setFilterModal] = useState(false);
  const [CourseOfferings, setCourseOfferings] = useState<CourseRegistrations[]>(
    []
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ id: string } | null>(null);
  type OptionType = { value: string; label: string };

  const statusOptions: OptionType[] = [
    { value: "New", label: "New" },
    { value: "Fee Requested", label: "Fee Requested" },
    { value: "In Progress", label: "In Progress" },
    { value: "Not Interested", label: "Not Interested" },
    { value: "On Hold", label: "On Hold" },
    { value: "Ready for batch", label: "Ready for batch" },
    { value: "Assigned to batch", label: "Assigned to batch" },
    { value: "Delete", label: "Delete" },
  ];

  // Set default selected values (first 3)
  const [selectedStatuses, setSelectedStatuses] = useState<MultiValue<OptionType>>(
    statusOptions.slice(0, 2) // Selecting first 3 options
  );
  const [CourseTypes, setCourseTypes] = useState<{ id: string; name: string }[]>([]);
  const [selectedCourseType, setSelectedCourseType] = useState<string>("");
  const [selectedGender, setSelectedGender] = useState("");
  const [searchTerm, setSearchTerm] = useState<string>("");

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const handleEditClick = (id: string) => {
    navigate("/admin/RegistrationForm", { state: { id } });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItem({ id });
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;

    try {
      await deleteCourseRegistrationApi(selectedItem.id, url);
      handleApiCallClick(currentPage);
      toast.success('Deleted successfully!');
    } catch (error) {
      toast.error("Failed to delete. " + error);
    } finally {
      setIsModalOpen(false);
      setSelectedItem(null);
    }
  };

  useEffect(() => {
    handleApiCallClick(currentPage);
  }, [currentPage]);

  const handleApiCallClick = async (
    page: number
  ) => {
    setLoader(true);

    try {
      const courseTypeData: any = await getCourseTypeApi(url);
      const courseTypeOptions = courseTypeData.map((courseType: any) => ({
        id: courseType.id,
        name: courseType.name,
        fees: courseType.fees
      }));
      setCourseTypes(courseTypeOptions);

      const selectedStatusValues = selectedStatuses.map((status) => status.value);
      const courseRegistrationData: any = await getCourseRegistrationApi(
        url,
        page,
        itemsPerPage,
        selectedStatusValues,
        selectedGender,
        selectedCourseType,
        searchTerm
      );


      if (courseRegistrationData) {

        const totalCount = courseRegistrationData.totalCount || 0;
        setTotalPages(Math.ceil(totalCount / itemsPerPage));

        const transformedData = courseRegistrationData.courseRegistration.map((item: any) => {
          return {
            ...item,
            user: {
              ...item.user,  // Keep existing user data
              gender: item.user?.gender === 1 ? "Male" : item.user?.gender === 2 ? "Female" : "",
            },
            paidOn: item.paidOn ? formatDate(item.paidOn) : "-",
            date: formatDate(item.updatedOn)
          };
        });


        setCourseOfferings(transformedData);
      }
    } catch (error) {
      toast.error("Failed to fetch course registration data. Please try again.");
      console.error(error);
    }
    finally {
      setLoader(false);
    }
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => {
      if (prevPage < totalPages) {
        return prevPage + 1;
      }
      return prevPage;
    });
  };

  const handleStatusChange = async (e, id: string, status: string, userId: string, offeringId: string | null,) => {
    await updateCourseRegistrationStatusApi(id, status, url);
    await handleApiCallClick(currentPage);
    toast.success(status)
  };

  // Search functions
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCurrentPage(1);
    handleApiCallClick(currentPage);
  };

  // filters
  const handleFilterStatusChange = (selected: MultiValue<OptionType>) => {
    // console.log("Selected Statuses:", selected);
    setSelectedStatuses(selected);
  };


  const handleGenderChange = (e) => {
    setSelectedGender(e.target.value);
  }

  const handleTypeChange = (e) => {
    setSelectedCourseType(e.target.value);
  }

  const clearAllFilters = () => {
    setSelectedStatuses(statusOptions.slice(0, 3)) // Selecting first 3 options)
    setSelectedGender("")
    setSelectedCourseType("")
  }

  const applyFilters = () => {
    setFilterModal(false);
    setCurrentPage(1);
    handleApiCallClick(currentPage);
  }



  return (
    <div className="px-2 md:px-4 mt-6">
      <h2 className="text-3xl font-bold text-blue-600 mb-5"> Registration</h2>

      {/* Search and Filter */}

      <div className="flex flex-row items-center w-full gap-4 justify-between md:justify-start">
        <form onSubmit={handleSearchSubmit} className="w-full md:flex-grow">
          <label
            htmlFor="default-search"
            className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white"
          >
            Search
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="search"
              id="default-search"
              className="w-full p-3 ps-10 outline-none text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Search by Name, Email, Mobile, Course name"
              value={searchTerm}
              onChange={handleSearchChange}
              required
            />
            <button
              type="submit"
              className="text-white absolute end-1.5 bottom-1.5 bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            >
              Search
            </button>
          </div>
        </form>
        <div
          className="flex flex-row gap-1 justify-center px-1 py-2 h-10 w-[90px] hover:bg-gray-100 rounded-md cursor-pointer"
          onClick={() => setFilterModal(true)}
        >
          <VscFilter className="text-blue-500 h-7 w-7" />
          <span className="text-lg font-normal text-gray-600">Filter</span>
        </div>
      </div>

      <div className="overflow-x-auto mt-5">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 md:rounded-lg">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Action</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Name</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Gender</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Email</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Mobile</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Fees</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Status</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Course Name</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Course Type</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Duration</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Preferred Timings</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Paid On</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Registered On</th>
              <th className="px-4 py-3.5 text-md font-normal text-left text-gray-500 dark:text-gray-400">Time Zone</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
            {/* Show loader row when loading */}
            {loader ? (
              <tr className="text-center">
                <td colSpan={12} className="py-3 text-center">
                  <div className="w-12 h-12 border-4 text-center border-t-blue-500 border-gray-300 rounded-full animate-spin mx-auto"></div>
                </td>
              </tr>
            ) : (
              CourseOfferings.map((item) => {
                return (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 text-sm whitespace-nowrap">
                      <div className="flex items-center gap-x-3">

                        <button
                          onClick={() => handleEditClick(item.id)}
                          className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user?.name}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user?.gender}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user?.email}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.user?.mobile}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.fee}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap min-w-[180px]">
                      <select
                        required
                        name="status"
                        value={item.status}
                        onChange={(e) =>
                          handleStatusChange(e, item.id, e.target.value, item.userId, item.courseOfferingId)
                        }
                        className="w-full border border-gray-400 bg-blue-50 rounded-lg px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >

                        {item.status === "New" && 
                        <>
                        <option value="New">New</option>
                        </>}

                        {item.status !== "Ready for batch" &&
                          item.status !== "Assigned to batch" &&
                          <>
                          <option value="Fee Requested">Fee Requested</option>
                          <option value="Delete">Delete</option>
                          </>}

                        {item.status === "Ready for batch" && (
                          <option value="Ready for batch">Ready for batch</option>
                        )} 

                        {item.status === "Assigned to batch" && (
                          <>
                            <option value="Assigned to batch">Assigned to batch</option>
                          </>
                        )}

                        {(item.status !== "Assigned to batch" && item.status !== "Ready for batch") && (
                          <>
                            <option value="In Progress">In Progress</option>
                            <option value="Not Interested">Not Interested</option>
                            <option value="On Hold">On Hold</option>
                          </>
                        )}
                      </select>
                    </td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.course?.name}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.courseType?.name}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.duration} Hour</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.timeRange}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.paidOn}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.date}</td>
                    <td className="px-4 py-4 text-sm font-normal text-gray-700 whitespace-nowrap">{item.timeZone}</td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>


      {/* Pagination */}
      <div className="flex flex-wrap items-center justify-center md:justify-between mt-6 gap-2 md:gap-4">
        {/* Previous Button */}
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
            />
          </svg>
          <span>Previous</span>
        </button>

        <div className="flex flex-wrap items-center justify-center gap-x-2">
          {/* Page Number */}

          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index + 1}
              onClick={() => setCurrentPage(index + 1)}
              className={`px-2 py-1 text-sm ${currentPage === index + 1
                ? "text-blue-500 bg-blue-100"
                : "text-gray-500 hover:bg-gray-100"
                } rounded-md`}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {/* NEXT Button */}
        <button
          // disabled={currentPage === totalPages}
          // onClick={() => setCurrentPage(currentPage + 1)}
          onClick={handleNextPage}
          className="flex items-center px-5 py-2 text-sm text-gray-700 capitalize transition-colors duration-200 bg-white border rounded-md gap-x-2 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-800"
        >
          <span>Next</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="w-5 h-5 rtl:-scale-x-100"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
            />
          </svg>
        </button>
      </div>

      {/* filter Modal */}
      <div className="inset-0 bg-gray-800 bg-opacity-50 z-50 transition-opacity duration-300">
        <div
          className={`fixed top-0 right-0 h-full lg:w-[500px] md:w-[500px] sm:w-full bg-white p-4 rounded-md shadow-lg transform transition-transform duration-500 ease-in-out ${filterModal ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <h1 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4 md:mb-6">
            Filter
          </h1>
          <button
            onClick={() => setFilterModal(false)}
            className="absolute top-1 right-3 text-gray-400 hover:text-gray-600 text-2xl"
          >
            &times;
          </button>

          <div className="flex items-center my-4">
            <div
              onClick={clearAllFilters}
              className=" cursor-pointer flex items-center gap-2 hover:bg-gray-200 py-1 rounded-sm"
            >
              <TbFilterX className="text-blue-500 h-5 w-5" />
              <span className="text-sm">Clear all</span>
            </div>
          </div>

          <div className="flex flex-col gap-3">

            <div className="mt-5">
              <label className="text-base font-semibold text-gray-900 mb-2">
                Status
              </label>
              <Select
                isMulti
                value={selectedStatuses}
                onChange={handleFilterStatusChange}
                options={statusOptions}
                className="w-[96%]"
                placeholder="Select Status"
                isClearable
                styles={{
                  control: (base, state) => ({
                    ...base,
                    borderColor: state.isFocused
                      ? "rgb(96 165 250)"
                      : "rgb(31 41 55)",
                    borderRadius: "0.180rem",
                    boxShadow: state.isFocused
                      ? "0 0 0 1px rgb(96 165 250)"
                      : "none",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "rgb(31 41 55)",
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "rgb(37 99 235)" : "white",
                    color: state.isFocused ? "white" : "black",
                    cursor: "pointer",
                    height: "30px",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "10px",
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: "rgb(31 41 55)",
                    ":hover": { color: "rgb(55 65 81)" },
                    svg: {
                      strokeWidth: "1",
                      width: "14px",
                      height: "14px",
                    },
                  }),
                  indicatorSeparator: () => null,
                }}
              />
            </div>

            <div className="flex flex-col">
              <div className="mb-4 flex flex-col">
                <label className="text-base font-semibold text-gray-900 mb-2">Gender</label>
                <div className="w-full">
                  <select
                    value={Number(selectedGender)}
                    onChange={handleGenderChange}
                    required
                    className="w-[96%] px-4 py-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  >
                    <option value="">All</option>
                    <option value="1">Male</option>
                    <option value="2">Female</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="mb-4 flex flex-col">
                <label className="text-base font-semibold text-gray-900 mb-2">Course Type</label>
                <div className="w-full">
                  <select
                    value={selectedCourseType}
                    onChange={handleTypeChange}
                    required
                    className="w-[96%] px-4 py-2 text-gray-800 bg-white border border-gray-400 rounded-md dark:bg-gray-800 dark:text-gray-300 dark:border-gray-800 focus:border-blue-400 focus:ring-blue-300 focus:ring-opacity-40 dark:focus:border-blue-300 focus:outline-none focus:ring"
                  >
                    <option value="">All</option>
                    {CourseTypes.map((courseType: { id: string; name: string }) => (
                      <option key={courseType.id} value={courseType.id}>
                        {courseType.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

          </div>

          <div className=" flex gap-2 md:gap-4 mt-5">
            <button
              onClick={applyFilters}
              className="mt-2 px-4 py-1 text-white border border-gray-800 rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              Apply
            </button>
            <button
              className="mt-2 px-4 py-1 border border-gray-800  rounded-sm hover:bg-gray-200"
              onClick={() => setFilterModal(false)}
            >
              Cancel
            </button>
          </div>

        </div>
      </div>

      {isModalOpen && (
        <DeleteItem onClose={() => setIsModalOpen(false)} onConfirm={confirmDelete} />
      )}

    </div>
  );
}
